import L from "leaflet";

/**
 * Camadas em canvas do relatório de KPIs de endereços externos (RELKpisEnderecosExternos).
 * Recebem as células de 0,001° de getmapakpisenderecosexternos, na posição média dos
 * endereços da célula ({lat, lng, validados, validos}).
 *
 * - CamadaCalorTaxaValidacao: "mapa de calor" da taxa de validação. A cor é a taxa da região
 *   (rejeitado → neutro → válido) e a opacidade cresce com a quantidade de endereços
 *   validados, em escala log; não é o calor de densidade do leaflet.heat, que só tem um
 *   gradiente por intensidade.
 * - CamadaPontosValidacao: um ponto por célula, círculo quando a maioria dos validados é
 *   válida e losango quando a maioria foi rejeitada.
 *
 * São GridLayers desenhados por tile: com dezenas de milhares de células, um circleMarker por
 * célula deixaria o mapa lento. Células sem nenhum endereço validado não entram (não há taxa).
 */

const TILE = 256;

function hexParaRgb(hex) {
  const h = String(hex || "").replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function misturar(a, b, t) {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/** Cor da taxa (0..1): rejeitado em 0, neutro em 0,5 e válido em 1. */
export function corDaTaxa(taxa, cores) {
  const t = Math.max(0, Math.min(1, taxa));
  return t < 0.5
    ? misturar(cores.rejeitado, cores.neutro, t * 2)
    : misturar(cores.neutro, cores.valido, (t - 0.5) * 2);
}

function coresRgb(opcoes) {
  return {
    valido: hexParaRgb(opcoes.corValido),
    rejeitado: hexParaRgb(opcoes.corRejeitado),
    neutro: hexParaRgb(opcoes.corNeutra),
  };
}

/**
 * Projeta as células no zoom pedido e as separa por tile; o cálculo é refeito só quando o
 * zoom ou os dados mudam.
 */
const IndicePorZoom = {
  setCelulas(celulas) {
    this._celulas = (celulas || []).filter((c) => Number(c.validados) > 0);
    this._cache = {};
    if (this._map) this.redraw();
    return this;
  },

  _indice(zoom) {
    if (this._cache && this._cache[zoom]) return this._cache[zoom];
    const tiles = new Map();
    for (const c of this._celulas || []) {
      const p = this._map.project([c.lat, c.lng], zoom);
      const ponto = { x: p.x, y: p.y, validados: Number(c.validados), validos: Number(c.validos) || 0, celula: c };
      const chave = Math.floor(p.x / TILE) + ":" + Math.floor(p.y / TILE);
      if (!tiles.has(chave)) tiles.set(chave, []);
      tiles.get(chave).push(ponto);
    }
    const indice = { tiles, extra: this._extraIndice ? this._extraIndice(tiles) : null };
    this._cache = this._cache || {};
    this._cache[zoom] = indice;
    return indice;
  },

  /** Pontos do tile e dos vizinhos (o desfoque e o raio do ponto passam da borda). */
  _pontosAoRedor(indice, coords) {
    const out = [];
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const lista = indice.tiles.get((coords.x + dx) + ":" + (coords.y + dy));
        if (lista) out.push(...lista);
      }
    }
    return out;
  },
};

export const CamadaCalorTaxaValidacao = L.GridLayer.extend({
  includes: IndicePorZoom,

  options: {
    tileSize: TILE,
    updateWhenZooming: false,
    /** Lado do quadrado de agregação, em pixels de tela. */
    bin: 16,
    /** Raio do desfoque, em bins. */
    raio: 2,
    opacidadeMin: 0.18,
    opacidadeMax: 0.75,
    corValido: "#2E7D32",
    corRejeitado: "#C62828",
    corNeutra: "#9E9E9E",
  },

  initialize(celulas, options) {
    L.setOptions(this, options);
    this._celulas = (celulas || []).filter((c) => Number(c.validados) > 0);
    this._cache = {};
  },

  /**
   * Referência de "muitos endereços" no zoom: percentil 95 dos bins ocupados (sem desfoque),
   * para um único bin muito cheio não apagar o resto do mapa.
   */
  _extraIndice(tiles) {
    const bin = this.options.bin;
    const somas = new Map();
    for (const lista of tiles.values()) {
      for (const p of lista) {
        const k = Math.floor(p.x / bin) + ":" + Math.floor(p.y / bin);
        somas.set(k, (somas.get(k) || 0) + p.validados);
      }
    }
    const valores = Array.from(somas.values()).sort((a, b) => a - b);
    const ref = valores.length ? valores[Math.floor((valores.length - 1) * 0.95)] : 1;
    return { referencia: Math.max(ref, 1) };
  },

  createTile(coords) {
    const tile = document.createElement("canvas");
    tile.width = TILE;
    tile.height = TILE;
    if (!this._map || !this._celulas || !this._celulas.length) return tile;

    const { bin, raio, opacidadeMin, opacidadeMax } = this.options;
    const indice = this._indice(coords.z);
    const pontos = this._pontosAoRedor(indice, coords);
    if (!pontos.length) return tile;

    // Grade do tile com margem de `raio` bins de cada lado, para o desfoque não ter costura.
    const lado = TILE / bin + raio * 2;
    const origemX = coords.x * TILE - raio * bin;
    const origemY = coords.y * TILE - raio * bin;
    const peso = new Float32Array(lado * lado);
    const bons = new Float32Array(lado * lado);
    let algum = false;
    for (const p of pontos) {
      const bx = Math.floor((p.x - origemX) / bin);
      const by = Math.floor((p.y - origemY) / bin);
      if (bx < 0 || by < 0 || bx >= lado || by >= lado) continue;
      peso[by * lado + bx] += p.validados;
      bons[by * lado + bx] += p.validos;
      algum = true;
    }
    if (!algum) return tile;

    const pesoD = desfocar(peso, lado, raio);
    const bonsD = desfocar(bons, lado, raio);

    const cores = coresRgb(this.options);
    const logRef = Math.log1p(indice.extra.referencia);
    const pequeno = document.createElement("canvas");
    pequeno.width = lado;
    pequeno.height = lado;
    const ctxP = pequeno.getContext("2d");
    const img = ctxP.createImageData(lado, lado);
    for (let i = 0; i < lado * lado; i++) {
      const w = pesoD[i];
      if (w <= 0) continue;
      const [r, g, b] = corDaTaxa(bonsD[i] / w, cores);
      const intensidade = Math.min(1, Math.log1p(w) / logRef);
      const o = i * 4;
      img.data[o] = r;
      img.data[o + 1] = g;
      img.data[o + 2] = b;
      img.data[o + 3] = Math.round(255 * (opacidadeMin + (opacidadeMax - opacidadeMin) * intensidade));
    }
    ctxP.putImageData(img, 0, 0);

    // Amplia a grade para o tile com suavização: é ela que dá o aspecto de "calor".
    const ctx = tile.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(pequeno, -raio * bin, -raio * bin, lado * bin, lado * bin);
    return tile;
  },
});

/** Média móvel separável (caixa de 2·raio+1) sobre a grade quadrada. */
function desfocar(src, lado, raio) {
  if (raio <= 0) return src;
  const tmp = new Float32Array(src.length);
  const out = new Float32Array(src.length);
  const area = raio * 2 + 1;
  for (let y = 0; y < lado; y++) {
    for (let x = 0; x < lado; x++) {
      let s = 0;
      for (let k = -raio; k <= raio; k++) {
        const xx = x + k;
        if (xx >= 0 && xx < lado) s += src[y * lado + xx];
      }
      tmp[y * lado + x] = s / area;
    }
  }
  for (let y = 0; y < lado; y++) {
    for (let x = 0; x < lado; x++) {
      let s = 0;
      for (let k = -raio; k <= raio; k++) {
        const yy = y + k;
        if (yy >= 0 && yy < lado) s += tmp[yy * lado + x];
      }
      out[y * lado + x] = s / area;
    }
  }
  return out;
}

export const CamadaPontosValidacao = L.GridLayer.extend({
  includes: IndicePorZoom,

  options: {
    tileSize: TILE,
    updateWhenZooming: false,
    raio: 3,
    corValido: "#2E7D32",
    corRejeitado: "#C62828",
    corNeutra: "#9E9E9E",
  },

  initialize(celulas, options) {
    L.setOptions(this, options);
    this._celulas = (celulas || []).filter((c) => Number(c.validados) > 0);
    this._cache = {};
  },

  createTile(coords) {
    const tile = document.createElement("canvas");
    tile.width = TILE;
    tile.height = TILE;
    if (!this._map || !this._celulas || !this._celulas.length) return tile;

    const pontos = this._pontosAoRedor(this._indice(coords.z), coords);
    const ox = coords.x * TILE;
    const oy = coords.y * TILE;
    const r = this.options.raio;
    const ctx = tile.getContext("2d");
    ctx.lineWidth = 0.75;
    ctx.strokeStyle = "rgba(0, 0, 0, 0.35)";

    const validos = [];
    const rejeitados = [];
    for (const p of pontos) {
      const x = p.x - ox;
      const y = p.y - oy;
      if (x < -r || y < -r || x > TILE + r || y > TILE + r) continue;
      (p.validos * 2 >= p.validados ? validos : rejeitados).push([x, y]);
    }

    // Rejeitados por baixo: os válidos, minoria na maior parte do mapa, ficam visíveis.
    ctx.fillStyle = this.options.corRejeitado;
    for (const [x, y] of rejeitados) {
      ctx.beginPath();
      ctx.moveTo(x, y - r);
      ctx.lineTo(x + r, y);
      ctx.lineTo(x, y + r);
      ctx.lineTo(x - r, y);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
    ctx.fillStyle = this.options.corValido;
    for (const [x, y] of validos) {
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
    return tile;
  },
});
